import Quill from 'quill';
import Delta from 'quill-delta';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useMarkdownConverter } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Card, Heading3, IconWrapper, Paragraph, QuillInput } from '@treams/treams-ui';

import WorkspaceInstructions from './WorkspaceInstructions';
import { GLOBAL_WORKSPACE_ID, prompts } from './constants';

const Circle = styled.div<{ color: string }>`
	min-width: 26px;
	min-height: 26px;
	border-radius: 50%;
	background-color: ${(props) =>
		props.color in props.theme.colors ? props.theme.colors[props.color] : props.color};

	display: flex;
	align-items: center;
	justify-content: center;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px 16px;
	max-width: 545px;
`;

export default function PromptInstructions() {
	const { t } = useTranslation();
	const { path } = useRouteMatch();
	const { push } = useHistory();

	const generalInstructionsRef = useRef<Quill | undefined>();
	const [setPromptInstruction] = graphql.useSetPromptInstructionMutation();
	const { markdownToQuillDelta, htmlToMarkdown } = useMarkdownConverter();
	const [generalInstructions, setGeneralInstructions] = useState<Delta | undefined>();

	const generalPromptInstructionQuery = graphql.usePromptInstructionQuery({
		variables: {
			workspaceID: GLOBAL_WORKSPACE_ID,
			promptInstructionID: graphql.Prompt.All,
		},
	});

	const saveInstructions = useCallback(
		async (ref: MutableRefObject<Quill | undefined>, workspaceID: string) => {
			if (!ref.current) return;

			const html = ref.current.getSemanticHTML();
			const instructions = htmlToMarkdown(html);

			await setPromptInstruction({
				variables: {
					input: {
						workspaceID,
						promptInstructionID: graphql.Prompt.All,
						instructions,
					},
				},
			});
		},
		[setPromptInstruction, htmlToMarkdown],
	);

	useEffect(() => {
		const instruction = generalPromptInstructionQuery.data?.promptInstruction;
		const delta = markdownToQuillDelta(instruction?.instructions ?? '');
		setGeneralInstructions(delta);
		generalInstructionsRef.current?.setContents(delta, 'api');
	}, [generalPromptInstructionQuery.data?.promptInstruction, markdownToQuillDelta]);

	return (
		<Box>
			<WorkspaceInstructions />
			<Heading3 mt={4} mb={1}>
				{t('settings.ai-workbench.general-instructions-title')}
			</Heading3>
			<Paragraph mb={3}>{t('settings.ai-workbench.general-instructions-description')}</Paragraph>
			{generalInstructions && (
				<QuillInput
					onQuill={(quillEditor) => {
						generalInstructionsRef.current = quillEditor;
					}}
					value={generalInstructions}
					onChange={setGeneralInstructions}
					hasHeading
					hideLink
					placeholder={t('settings.ai-workbench.enter-instructions')}
				/>
			)}
			<Button
				title={t('common.save')}
				mt={2}
				onClick={() => saveInstructions(generalInstructionsRef, GLOBAL_WORKSPACE_ID)}
			/>
			<Heading3 mt={4} mb={1}>
				{t('settings.ai-workbench.prompts')}
			</Heading3>
			<Paragraph mb={3}>{t('settings.ai-workbench.custom-instructions-description')}</Paragraph>
			<Grid>
				{Object.values(prompts).map(({ slug, title, color, icon }) => (
					<Card key={slug} p={12} onClick={() => push(`${path}/${slug}`)}>
						<Flex alignItems="center">
							<Circle color={color}>
								<IconWrapper size={16} color="white">
									{icon}
								</IconWrapper>
							</Circle>
							<Heading3 ml={2}>{title}</Heading3>
						</Flex>
					</Card>
				))}
			</Grid>
		</Box>
	);
}
